import {
    interval,
    Observable,
    BehaviorSubject,
    Subject,
    firstValueFrom,
    NEVER,
    timer,
    of
} from 'rxjs';
import {
    startWith,
    takeUntil,
    switchMap,
    debounceTime,
    share,
    map,
    catchError
} from 'rxjs/operators';
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import logger from 'loglevel';

import { TeamId } from '@flb/common/team-id';
import { TrainingSessionId } from '@flb/common/training-session-transport';
import { TrainingEvent } from '@flb/common/event-history/training-event';
import { shareConfigPublishReplay } from '@flb/common/share-config-publish-replay';
import { arrayCompare, assertInstanceOf, objectCompare } from '@flb/common/util';
import { AuthToken, beforeExpiry } from '@flb/common/auth-token';
import { AccessLevel } from '@flb/common/access-level';
import { NotifyService } from '@neutrino/core/notify';

export interface StoryBoardState {
    showTargetBindings: boolean;
    showObjectives: { [group: string]: boolean };
    changes: any;
}

export interface EventHistoryState {
    selectedEvent: TrainingEvent;
    selectedSessions: string[];
    selectedTeams: Array<[TrainingSessionId, TeamId]>;
}

export interface DiagnosticsState {
    loadTestTeamCount: number;
    loadTestBandwidth: number;
    loadTestLatency: number;
    loadTestCompletionChance: number;
    randomizeTimes: number;
}
/**
 * Client state that is stored in localStorage. This is shared between all sessions
 */
interface ClientState {
    authToken: AuthToken | null;
    storyBoardState: StoryBoardState;
    eventHistoryState: EventHistoryState;
    diagnosticsState: DiagnosticsState;
}

/**
 * Client state that is stored in sessionStorage. This data is unique for each browser tab/window
 */
interface ClientSessionState {
    debugControls: boolean;
    leaderboardPage: number;
    selectedTeams: number[];
    // lb pagination and scroll options
    selectedPage: number;
    autoScroll: boolean;
    autoScrollTime: number;
    teamsPerPage: number;
    // lb team scroll options
    selectedTeam: number;
    pinnedTeams: number;
    pinnedAutoScroll: boolean;
    pinnedAutoScrollTime: number;
    // lb layout options
    noFullscreen: boolean;
    menuEnabled: boolean;
    useAspectRatio: boolean;
    aspectRatio: number;
    useActionSafe: boolean;
    actionSafeZoom: number;
}

/**
 * This service persists data and handles logic related to the client state for admin/readonly users.
 */
@Injectable()
export class ClientStateService implements OnDestroy {
    private store: ClientState = {
        authToken: null,
        storyBoardState: <StoryBoardState>{},
        eventHistoryState: <EventHistoryState>{},
        diagnosticsState: {
            loadTestTeamCount: 100,
            loadTestBandwidth: 5,
            loadTestLatency: 150,
            loadTestCompletionChance: 10,
            randomizeTimes: 1
        }
    };
    private sessionStore: ClientSessionState = {
        debugControls: false,
        leaderboardPage: 1,
        selectedPage: 0,
        selectedTeam: 0,
        noFullscreen: false,
        menuEnabled: true,
        autoScroll: true,
        autoScrollTime: 15,
        teamsPerPage: 10,
        pinnedTeams: 3,
        pinnedAutoScroll: true,
        pinnedAutoScrollTime: 5,
        selectedTeams: <number[]>[],
        useActionSafe: false,
        actionSafeZoom: 90,
        useAspectRatio: false,
        aspectRatio: 1.5
    };
    // don't persist this. user must enable after every refresh
    public debugControls = new BehaviorSubject(this.sessionStore.debugControls);
    public updateInterval = interval(5000).pipe(startWith(0), share(shareConfigPublishReplay()));
    public adminPassword = new BehaviorSubject<string>('');
    public readonlyPassword = new BehaviorSubject<string>('');
    public playerPassword = new BehaviorSubject<string>('');
    public viewerPassword = new BehaviorSubject<string>('');
    public authToken = new BehaviorSubject<AuthToken | null>(null);
    public adminAccess: Observable<boolean>;
    public readonlyAccess: Observable<boolean>;
    public playerAccess: Observable<boolean>;
    public adminOrReadonlyAccess: Observable<boolean>;
    public storyBoardState = new BehaviorSubject<StoryBoardState>(<StoryBoardState>{});
    public eventHistoryState = new BehaviorSubject<EventHistoryState>(<EventHistoryState>{});
    public diagnosticsState = new BehaviorSubject<DiagnosticsState>(this.store.diagnosticsState);
    public leaderboardPage = new BehaviorSubject<number>(this.sessionStore.leaderboardPage);
    public selectedPage = new BehaviorSubject<number>(this.sessionStore.selectedPage);
    public selectedTeam = new BehaviorSubject<number>(this.sessionStore.selectedTeam);
    public noFullscreen = new BehaviorSubject<boolean>(this.sessionStore.noFullscreen);
    public menuEnabled = new BehaviorSubject(this.sessionStore.menuEnabled);
    public autoScroll = new BehaviorSubject<boolean>(this.sessionStore.autoScroll);
    public autoScrollTime = new BehaviorSubject<number>(this.sessionStore.autoScrollTime);
    public teamsPerPage = new BehaviorSubject<number>(this.sessionStore.teamsPerPage);
    public pinnedTeams = new BehaviorSubject<number>(this.sessionStore.pinnedTeams);
    public pinnedAutoScroll = new BehaviorSubject<boolean>(this.sessionStore.pinnedAutoScroll);
    public pinnedAutoScrollTime = new BehaviorSubject<number>(
        this.sessionStore.pinnedAutoScrollTime
    );
    public useActionSafe = new BehaviorSubject<boolean>(this.sessionStore.useActionSafe);
    public actionSafeZoom = new BehaviorSubject<number>(this.sessionStore.actionSafeZoom);
    public useAspectRatio = new BehaviorSubject<boolean>(this.sessionStore.useAspectRatio);
    public aspectRatio = new BehaviorSubject<number>(this.sessionStore.aspectRatio);

    public selectedTeams = new BehaviorSubject<number[]>(this.sessionStore.selectedTeams || []);
    private destroyed = new Subject<void>();
    // shared auth token check observable. It's cold until you subscribe, then it will cache the
    // result of the check for a while
    private authTokenChecks = this.authToken.pipe(
        switchMap(authToken =>
            authToken
                ? this.http.post<AuthToken>(`/api/auth/renew`, { tokenId: authToken.id }).pipe(
                      catchError((err: unknown) => {
                          assertInstanceOf(err, HttpErrorResponse);
                          return of(err);
                      })
                  )
                : of(null)
        ),
        takeUntil(this.destroyed),
        share({
            ...shareConfigPublishReplay(),
            // share the check result until before it expires or 1 second if we don't have a token yet
            resetOnRefCountZero: () =>
                this.authToken.value
                    ? timer(beforeExpiry(this.authToken.value.expiry, 0.5))
                    : of(true)
        })
    );
    constructor(
        private http: HttpClient,
        private notify: NotifyService
    ) {
        const store = localStorage.getItem('ClientState');
        const sessionStore = sessionStorage.getItem('ClientState');
        if (store) {
            try {
                Object.assign(this.store, JSON.parse(store));
            } catch (ex) {
                logger.log('While restoring ClientState:', ex);
            }
        }
        if (sessionStore) {
            try {
                Object.assign(this.sessionStore, JSON.parse(sessionStore));
                if (!this.sessionStore.leaderboardPage || this.sessionStore.autoScroll) {
                    this.sessionStore.leaderboardPage = 1;
                }
            } catch (ex) {
                logger.log(`While restoring ClientState (session)`, ex);
            }
        }
        this.debugControls.next(this.sessionStore.debugControls);
        this.authToken.next(this.store.authToken || null);
        this.diagnosticsState.next(this.store.diagnosticsState);
        this.leaderboardPage.next(this.sessionStore.leaderboardPage);
        this.selectedPage.next(this.sessionStore.selectedPage);
        this.selectedTeam.next(this.sessionStore.selectedTeam);
        this.noFullscreen.next(this.sessionStore.noFullscreen);
        this.menuEnabled.next(this.sessionStore.menuEnabled);
        this.autoScroll.next(this.sessionStore.autoScroll);
        this.autoScrollTime.next(this.sessionStore.autoScrollTime);
        this.teamsPerPage.next(this.sessionStore.teamsPerPage);
        this.pinnedTeams.next(this.sessionStore.pinnedTeams);
        this.pinnedAutoScroll.next(this.sessionStore.pinnedAutoScroll);
        this.pinnedAutoScrollTime.next(this.sessionStore.pinnedAutoScrollTime);
        this.selectedTeams.next(this.sessionStore.selectedTeams || []);
        this.useActionSafe.next(this.sessionStore.useActionSafe);
        this.actionSafeZoom.next(this.sessionStore.actionSafeZoom);
        this.useAspectRatio.next(this.sessionStore.useAspectRatio);
        this.aspectRatio.next(this.sessionStore.aspectRatio);

        this.storyBoardState.next(this.store.storyBoardState);
        this.eventHistoryState.next(this.store.eventHistoryState);
        this.debugControls
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                this.sessionStore.debugControls = value;
                this.saveSession();
            })
            .catch(logger.error);
        this.adminPassword
            .pipe(debounceTime(500), takeUntil(this.destroyed))
            .forEach(value => {
                this.authenticateAdmin(value)
                    .then(result => {
                        if (result) {
                            this.authToken.next(result);
                        }
                    })
                    .catch(logger.error);
            })
            .catch(logger.error);
        this.readonlyPassword
            .pipe(debounceTime(500), takeUntil(this.destroyed))
            .forEach(value => {
                this.authenticateReadonly(value)
                    .then(result => {
                        if (result) {
                            this.authToken.next(result);
                        }
                    })
                    .catch(logger.error);
            })
            .catch(logger.error);
        this.playerPassword
            .pipe(debounceTime(500), takeUntil(this.destroyed))
            .forEach(value => {
                this.authenticatePlayer(value)
                    .then(result => {
                        if (result) {
                            this.authToken.next(result);
                        }
                    })
                    .catch(logger.error);
            })
            .catch(logger.error);
        this.viewerPassword
            .pipe(debounceTime(500), takeUntil(this.destroyed))
            .forEach(value => {
                this.authenticateViewer(value)
                    .then(result => {
                        if (result) {
                            this.authToken.next(result);
                        }
                    })
                    .catch(logger.error);
            })
            .catch(logger.error);
        this.authToken
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                this.store.authToken = value;
                this.save();
            })
            .catch(logger.error);
        this.authToken
            .pipe(
                switchMap(t => (t ? timer(beforeExpiry(t?.expiry)).pipe(map(() => t)) : NEVER)),
                takeUntil(this.destroyed)
            )
            .forEach(t => {
                this.renewToken(t).catch(logger.error);
            })
            .catch(logger.error);
        this.adminAccess = this.authTokenChecks.pipe(
            map(t => (t && 'accessLevel' in t && t.accessLevel === AccessLevel.Admin) || false)
        );
        this.readonlyAccess = this.authTokenChecks.pipe(
            map(t => (t && 'accessLevel' in t && t.accessLevel === AccessLevel.Readonly) || false)
        );
        this.playerAccess = this.authTokenChecks.pipe(
            map(t => (t && 'accessLevel' in t && t.accessLevel === AccessLevel.Player) || false)
        );
        this.adminOrReadonlyAccess = this.authTokenChecks.pipe(
            map(
                t =>
                    (t &&
                        'accessLevel' in t &&
                        [AccessLevel.Admin, AccessLevel.Readonly].includes(t.accessLevel)) ||
                    false
            )
        );
        this.storyBoardState
            .pipe(debounceTime(100), takeUntil(this.destroyed))
            .forEach(value => {
                // TODO: deep value compare?
                const sbs = this.store.storyBoardState;
                const oldGroups = sbs.showObjectives ? Object.keys(sbs.showObjectives) : [];
                const newGroups = value.showObjectives ? Object.keys(value.showObjectives) : [];
                const newChanges = JSON.stringify(value.changes || null);
                const oldChanges = JSON.stringify(sbs.changes || null);
                if (
                    sbs.showTargetBindings !== value.showTargetBindings ||
                    oldGroups.length !== newGroups.length ||
                    newGroups.some(k => sbs.showObjectives[k] !== value.showObjectives[k]) ||
                    oldChanges !== newChanges
                ) {
                    // ensure that the contents of 'changes' will definitely not
                    // refer to any previous object instances
                    this.store.storyBoardState = {
                        ...value,
                        ...{ changes: JSON.parse(newChanges) }
                    };
                    this.save();
                }
            })
            .catch(logger.error);
        this.eventHistoryState
            .pipe(debounceTime(100), takeUntil(this.destroyed))
            .forEach(value => {
                this.store.eventHistoryState = value;
                this.save();
            })
            .catch(logger.error);
        this.leaderboardPage
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                if (value !== this.sessionStore.leaderboardPage) {
                    this.sessionStore.leaderboardPage = value;
                    this.saveSession();
                }
            })
            .catch(logger.error);
        this.selectedPage
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                if (value !== this.sessionStore.selectedPage) {
                    this.sessionStore.selectedPage = value;
                    this.saveSession();
                }
            })
            .catch(logger.error);
        this.selectedTeam
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                if (value !== this.sessionStore.selectedTeam) {
                    this.sessionStore.selectedTeam = value;
                    this.saveSession();
                }
            })
            .catch(logger.error);
        this.noFullscreen
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                if (value !== this.sessionStore.noFullscreen) {
                    this.sessionStore.noFullscreen = value;
                    this.saveSession();
                }
            })
            .catch(logger.error);
        this.menuEnabled
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                if (value !== this.sessionStore.menuEnabled) {
                    this.sessionStore.menuEnabled = value;
                    this.saveSession();
                }
            })
            .catch(logger.error);
        this.autoScroll
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                if (value !== this.sessionStore.autoScroll) {
                    this.sessionStore.autoScroll = value;
                    this.saveSession();
                }
            })
            .catch(logger.error);
        this.autoScrollTime
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                if (value !== this.sessionStore.autoScrollTime) {
                    this.sessionStore.autoScrollTime = value;
                    this.saveSession();
                }
            })
            .catch(logger.error);
        this.teamsPerPage
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                if (value !== this.sessionStore.teamsPerPage) {
                    this.sessionStore.teamsPerPage = value;
                    this.saveSession();
                }
            })
            .catch(logger.error);
        this.pinnedTeams
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                if (value !== this.sessionStore.pinnedTeams) {
                    this.sessionStore.pinnedTeams = value;
                    this.saveSession();
                }
            })
            .catch(logger.error);
        this.pinnedAutoScroll
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                if (value !== this.sessionStore.pinnedAutoScroll) {
                    this.sessionStore.pinnedAutoScroll = value;
                    this.saveSession();
                }
            })
            .catch(logger.error);
        this.pinnedAutoScrollTime
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                if (value !== this.sessionStore.pinnedAutoScrollTime) {
                    this.sessionStore.pinnedAutoScrollTime = value;
                    this.saveSession();
                }
            })
            .catch(logger.error);
        this.selectedTeams
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                if (
                    !this.sessionStore.selectedTeams ||
                    !arrayCompare(value, this.sessionStore.selectedTeams)
                ) {
                    this.sessionStore.selectedTeams = value;
                    this.saveSession();
                }
            })
            .catch(logger.error);
        this.useActionSafe
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                if (value !== this.sessionStore.useActionSafe) {
                    this.sessionStore.useActionSafe = value;
                    this.saveSession();
                }
            })
            .catch(logger.error);
        this.actionSafeZoom
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                if (value !== this.sessionStore.actionSafeZoom) {
                    this.sessionStore.actionSafeZoom = value;
                    this.saveSession();
                }
            })
            .catch(logger.error);
        this.useAspectRatio
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                if (value !== this.sessionStore.useAspectRatio) {
                    this.sessionStore.useAspectRatio = value;
                    this.saveSession();
                }
            })
            .catch(logger.error);
        this.aspectRatio
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                if (value !== this.sessionStore.aspectRatio) {
                    this.sessionStore.aspectRatio = value;
                    this.saveSession();
                }
            })
            .catch(logger.error);
        this.diagnosticsState
            .pipe(takeUntil(this.destroyed))
            .forEach(value => {
                if (!objectCompare(value, this.store.diagnosticsState)) {
                    this.store.diagnosticsState = value;
                    this.save();
                }
            })
            .catch(logger.error);
    }

    authenticateViewer(password: string) {
        return this.authenticate(password, 'viewer');
    }

    private authenticatePlayer(password: string) {
        return this.authenticate(password, 'player');
    }

    private authenticateReadonly(password: string) {
        return this.authenticate(password, 'readonly');
    }

    private authenticateAdmin(password: string) {
        return this.authenticate(password);
    }

    private async authenticate(password: string, path: '' | 'readonly' | 'player' | 'viewer' = '') {
        try {
            return password
                ? (
                      await firstValueFrom(
                          this.http.post<{ token: AuthToken }>(
                              `/api/auth${path ? `/${path}` : ''}`,
                              { password },
                              {}
                          )
                      )
                  ).token
                : null;
        } catch (response) {
            if (response instanceof HttpErrorResponse) {
                const error = response.error || response;
                logger.log(error.message || error);
                this.notify.error(error.message || response.statusText, { duration: 5000 });
            } else {
                logger.error('Unexpected exception type', response);
                throw new Error(
                    `Unexpected exception type ${
                        (response && typeof response === 'object' && response?.constructor?.name) ||
                        response
                    }`
                );
            }
            return null;
        }
    }

    authHeaders() {
        return {
            Authorization: `Bearer ${this.authToken.value?.id || ''}`
        };
    }

    checkAdminToken(silent = true) {
        return this.checkToken([AccessLevel.Admin], silent);
    }

    checkAdminOrReadonlyToken(silent = true) {
        return this.checkToken([AccessLevel.Admin, AccessLevel.Readonly], silent);
    }

    checkAdminOrReadonlyOrViewToken(silent = true) {
        return this.checkToken(
            [AccessLevel.Admin, AccessLevel.Readonly, AccessLevel.Viewer],
            silent
        );
    }

    checkPlayerToken(silent = true) {
        return this.checkToken([AccessLevel.Player], silent);
    }

    async renewToken(token: AuthToken) {
        try {
            const newToken = await firstValueFrom(
                this.http.post<AuthToken>(`/api/auth/renew`, { tokenId: token.id })
            );
            this.authToken.next(newToken);
            return true;
        } catch {
            return false;
        }
    }

    async checkToken(accessLevels: AccessLevel[], silent = false) {
        const authToken = this.authToken.value;
        try {
            if (!authToken || !accessLevels.includes(authToken.accessLevel)) {
                return false;
            }
            const tokenOrError = await firstValueFrom(this.authTokenChecks);
            if (tokenOrError instanceof HttpErrorResponse) {
                if (!silent) {
                    this.notify.error(tokenOrError.statusText, { duration: 5000 });
                }
                logger.error('Error checking token: ', tokenOrError);
                return false;
            } else {
                const correctAccessLevel =
                    tokenOrError && accessLevels.includes(tokenOrError.accessLevel);
                if (!correctAccessLevel) {
                    const message = `Token has incorrect access level ${tokenOrError?.accessLevel}, requested ${accessLevels}`;
                    if (!silent) {
                        this.notify.error(message);
                    }
                    logger.error(message);
                }
                return correctAccessLevel;
            }
        } catch (ex) {
            this.authToken.next(null);
            logger.warn('Auth token not found:', ex);
            return false;
        }
    }

    logOut() {
        this.authToken.next(null);
    }

    ngOnDestroy(): void {
        this.destroyed.next();
        this.destroyed.complete();
    }

    private save() {
        localStorage.setItem('ClientState', JSON.stringify(this.store));
    }

    private saveSession() {
        sessionStorage.setItem('ClientState', JSON.stringify(this.sessionStore));
    }
}
